import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Button, Card, Empty, Input, Space, Spin, Typography } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import type { NewsFlashItem, NewsFlashResponse } from '../../types/api'
import KeywordText from './KeywordText'

const { Text, Paragraph } = Typography

const REFRESH_MS = 3 * 60 * 1000

type NewsFlashTabProps = {
  enabled: boolean
  isMobile: boolean
  fetchNews: (limit?: number) => Promise<NewsFlashResponse>
}

function formatTime(iso?: string | null): string {
  if (!iso) return '-'
  const d = dayjs(iso)
  return d.isValid() ? d.format('MM-DD HH:mm') : '-'
}

function matchesSearch(item: NewsFlashItem, q: string): boolean {
  const needle = q.trim().toLowerCase()
  if (!needle) return true
  return (
    item.title.toLowerCase().includes(needle) ||
    item.summary.toLowerCase().includes(needle)
  )
}

function NewsCard({
  item,
  searchQuery,
  isMobile,
}: {
  item: NewsFlashItem
  searchQuery: string
  isMobile: boolean
}) {
  const extraKw = searchQuery.trim() ? [searchQuery.trim()] : []
  const openLink = () => {
    if (item.link) window.open(item.link, '_blank', 'noopener,noreferrer')
  }

  return (
    <Card
      size="small"
      className="news-flash-card"
      hoverable
      onClick={openLink}
      bodyStyle={{ padding: isMobile ? '10px 12px' : '12px 16px' }}
    >
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {formatTime(item.published_at)}
        </Text>
        <Text strong style={{ fontSize: isMobile ? 14 : 15, lineHeight: 1.45 }}>
          <KeywordText text={item.title} extraKeywords={extraKw} />
        </Text>
        {item.summary ? (
          <Paragraph
            type="secondary"
            ellipsis={{ rows: isMobile ? 2 : 3 }}
            style={{ marginBottom: 4, fontSize: 13 }}
          >
            <KeywordText text={item.summary} extraKeywords={extraKw} />
          </Paragraph>
        ) : null}
        <Text type="link" style={{ fontSize: 12 }} onClick={(e) => { e.stopPropagation(); openLink() }}>
          阅读原文 →
        </Text>
      </Space>
    </Card>
  )
}

export default function NewsFlashTab({ enabled, isMobile, fetchNews }: NewsFlashTabProps) {
  const [search, setSearch] = React.useState('')

  const { data, isLoading, isFetching, dataUpdatedAt, refetch, error } = useQuery({
    queryKey: ['news-flash'],
    queryFn: () => fetchNews(50),
    enabled,
    staleTime: REFRESH_MS,
    refetchInterval: enabled ? REFRESH_MS : false,
    refetchIntervalInBackground: false,
  })

  const filtered = React.useMemo(() => {
    const items = data?.items || []
    return items.filter((item) => matchesSearch(item, search))
  }, [data?.items, search])

  const total = data?.total ?? data?.items?.length ?? 0

  return (
    <div className="news-flash-tab">
      <Space
        direction={isMobile ? 'vertical' : 'horizontal'}
        style={{ width: '100%', marginBottom: 12, justifyContent: 'space-between' }}
        wrap
      >
        <Input.Search
          allowClear
          placeholder="搜索快讯标题或摘要…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: isMobile ? '100%' : 320, width: isMobile ? '100%' : undefined }}
        />
        <Space size="small" wrap>
          <Text type="secondary" style={{ fontSize: 12 }}>
            共 {filtered.length}{search ? ` / ${total}` : ''} 条
            {data?.stale ? ' · 缓存数据' : ''}
            {dataUpdatedAt ? ` · 更新于 ${dayjs(dataUpdatedAt).format('HH:mm:ss')}` : ''}
          </Text>
          <Button
            size="small"
            icon={<ReloadOutlined spin={isFetching && !isLoading} />}
            onClick={() => void refetch()}
            loading={isLoading}
          >
            刷新
          </Button>
        </Space>
      </Space>

      {isLoading && !data ? (
        <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>
      ) : error ? (
        <Empty description="快讯加载失败，请稍后重试" />
      ) : filtered.length === 0 ? (
        <Empty description={search ? '无匹配快讯' : '暂无快讯'} />
      ) : (
        <div className="news-flash-list">
          {filtered.map((item) => (
            <NewsCard
              key={item.id}
              item={item}
              searchQuery={search}
              isMobile={isMobile}
            />
          ))}
        </div>
      )}
    </div>
  )
}
